import {
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
    ReactNode,
} from 'react';
import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export enum NumberStepperType {
    PERCENTAGE = 'percentage',
    NUMBER = 'number',
}

interface NumberStepperProps {
    value: number;
    onChange: (value: number) => void;
    type?: NumberStepperType;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    className?: string;
}

interface StepperButtonProps {
    onClick: () => void;
    disabled: boolean;
    ariaLabel: string;
    icon: ReactNode;
}

const DEFAULT_VALUES = {
    MIN: 0,
    MAX: 100,
    STEP: 1,
} as const;

const ICON_SIZE = 12;

const clampNumber = (value: number, min: number, max: number): number => {
    return Math.max(min, Math.min(max, value));
};

const formatDisplayValue = (
    value: number,
    type: NumberStepperType,
): string | number => {
    return type === NumberStepperType.PERCENTAGE ? `${value}%` : value;
};

function StepperButton({
    onClick,
    disabled,
    ariaLabel,
    icon,
}: StepperButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
                'flex size-6 items-center justify-center rounded-[2px] transition-colors bg-grey-550 text-grey-200 hover:bg-grey-400 hover:text-white disabled:bg-grey-600 disabled:text-grey-300 disabled:cursor-not-allowed',
            )}
        >
            {icon}
        </button>
    );
}

export function NumberStepper({
    value,
    onChange,
    type = NumberStepperType.PERCENTAGE,
    min = DEFAULT_VALUES.MIN,
    max = DEFAULT_VALUES.MAX,
    step = DEFAULT_VALUES.STEP,
    disabled = false,
    className,
}: NumberStepperProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(String(value));
    const inputRef = useRef<HTMLInputElement>(null);

    const clampedValue = useMemo(
        () => clampNumber(value, min, max),
        [value, min, max],
    );
    const displayValue = useMemo(
        () => formatDisplayValue(clampedValue, type),
        [clampedValue, type],
    );
    const isDecrementDisabled = disabled || clampedValue <= min;
    const isIncrementDisabled = disabled || clampedValue >= max;

    useEffect(() => {
        if (clampedValue !== value) onChange(clampedValue);
        if (!isEditing) setInputValue(String(clampedValue));
    }, [clampedValue, value, isEditing]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleDecrement = useCallback(
        () => onChange(clampNumber(clampedValue - step, min, max)),
        [clampedValue, step, min, max, onChange],
    );

    const handleIncrement = useCallback(
        () => onChange(clampNumber(clampedValue + step, min, max)),
        [clampedValue, step, min, max, onChange],
    );

    const startEditing = useCallback(
        () => !disabled && setIsEditing(true),
        [disabled],
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(e.target.value),
        [],
    );

    const commitValue = useCallback(() => {
        const parsedValue = parseInt(inputValue, 10);
        if (!isNaN(parsedValue)) onChange(clampNumber(parsedValue, min, max));
        setIsEditing(false);
    }, [inputValue, min, max, onChange]);

    const cancelEditing = useCallback(() => {
        setInputValue(String(clampedValue));
        setIsEditing(false);
    }, [clampedValue]);

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                commitValue();
                e.stopPropagation();
            } else if (e.key === 'Escape') {
                cancelEditing();
                e.stopPropagation();
            }
        },
        [commitValue, cancelEditing],
    );

    return (
        <div
            role="group"
            aria-label="Number stepper"
            className={cn(
                'flex h-8 items-center gap-2.5 border border-grey-500 rounded-[2px] pl-1.5 pr-1',
                disabled && 'border-grey-600 bg-grey-650',
                className,
            )}
        >
            {isEditing ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={commitValue}
                    onKeyDown={handleInputKeyDown}
                    aria-live="polite"
                    className={cn(
                        'font-geist-mono text-xs bg-transparent outline-none text-white-100',
                        `w-12`,
                    )}
                />
            ) : (
                <div
                    onClick={startEditing}
                    aria-live="polite"
                    className={cn(
                        'font-geist-mono text-xs cursor-text',
                        disabled ? 'text-grey-300' : 'text-white-100',
                        `w-12`,
                    )}
                >
                    {displayValue}
                </div>
            )}

            <div className="ml-auto flex items-center gap-1">
                <StepperButton
                    onClick={handleDecrement}
                    disabled={isDecrementDisabled}
                    ariaLabel="Decrease value"
                    icon={<MinusIcon size={ICON_SIZE} />}
                />
                <StepperButton
                    onClick={handleIncrement}
                    disabled={isIncrementDisabled}
                    ariaLabel="Increase value"
                    icon={<PlusIcon size={ICON_SIZE} />}
                />
            </div>
        </div>
    );
}
